import { FormattedDateTime } from "@dgs/core";
import { FC } from "react";
import styled from "styled-components";
import { TravelSegmentType } from "../../../../shared/types/travelSegment";
import {
	TravelTemplateIndexResource,
	TravelTemplateIndexSegmentResource,
} from "../../../../shared/types/travelTemplate";
import { BaseCard, Information, Location, TravelIcon } from "./SelectableTravelSegmentBaseCard";
import { ContingentTag } from "./SelectableTravelSegmentCard";

interface Props {
	travelTemplate: TravelTemplateIndexResource;
}

const Container = styled(BaseCard)`
	display: grid;
	position: relative;
	grid-template-columns: 1fr 3fr 3fr;
	min-height: 132px;
	gap: ${({ theme }) => theme.spacing(5)};
	justify-content: center;
`;

const TravelTemplateIconWithBadgeContainer = styled.div`
	position: relative;
	display: inline-block;
`;
const TravelTemplateIconContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;
const Badge = styled.div`
	position: absolute;
	bottom: -8px;
	right: -8px;
	background-color: ${({ theme }) => theme.colors.palette.primary.main.base};
	color: ${({ theme }) => theme.colors.palette.primary.main.contrast};
	width: 6px;
	height: 6px;
	padding: 8px;
	border-radius: 50%;
	box-sizing: content-box;
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: 14px;
	font-weight: bold;
`;

export const SelectableTravelTemplateSegmentCard: FC<Props> = ({ travelTemplate }) => {
	return (
		<Container $hasNoContingent={travelTemplate.hasSegmentFullContingent}>
			<TravelTemplateIconContainer>
				<TravelTemplateIconWithBadgeContainer>
					{travelTemplate.primaryTravelSegmentType && (
						<TravelTemplateIcon type={travelTemplate.primaryTravelSegmentType} />
					)}

					{!travelTemplate.primaryTravelSegmentType && <TravelIcon src="/icons/trip.svg" />}
					<Badge>{travelTemplate.travelSegmentsCount}</Badge>
				</TravelTemplateIconWithBadgeContainer>
			</TravelTemplateIconContainer>
			<Information>
				{travelTemplate.firstTravelSegment.type === "car" && travelTemplate.direction === "arrival" ? (
					"-"
				) : (
					<FormattedDateTime
						dateTime={`${travelTemplate.firstTravelSegment.departureDate}T${travelTemplate.firstTravelSegment.departureTime}`}
					/>
				)}
				<Location title={getTravelTemplateDepartureLocation(travelTemplate.firstTravelSegment)}>
					{getTravelTemplateDepartureLocation(travelTemplate.firstTravelSegment)}
				</Location>
			</Information>
			<Information>
				{travelTemplate.lastTravelSegment.type === "car" && travelTemplate.direction === "departure" ? (
					"-"
				) : (
					<FormattedDateTime
						dateTime={`${travelTemplate.lastTravelSegment.arrivalDate}T${travelTemplate.lastTravelSegment.arrivalTime}`}
					/>
				)}
				<Location title={getTravelTemplateArrivalLocation(travelTemplate.lastTravelSegment)}>
					{getTravelTemplateArrivalLocation(travelTemplate.lastTravelSegment)}
				</Location>
			</Information>
			{travelTemplate.hasSegmentFullContingent && <ContingentTag />}
		</Container>
	);
};
const getTravelTemplateDepartureLocation = (travelSegment: TravelTemplateIndexSegmentResource) => {
	switch (travelSegment.type) {
		case "flight":
			return `(${travelSegment.departureLocation.iataCode}) ${travelSegment.departureLocation.name}`;
		default:
			return travelSegment.departureLocation || "-";
	}
};
const getTravelTemplateArrivalLocation = (travelSegment: TravelTemplateIndexSegmentResource) => {
	switch (travelSegment.type) {
		case "flight":
			return `(${travelSegment.arrivalLocation.iataCode}) ${travelSegment.arrivalLocation.name}`;
		default:
			return travelSegment.arrivalLocation || "-";
	}
};
const TravelTemplateIcon: FC<{ type: TravelSegmentType }> = ({ type }) => {
	switch (type) {
		case "train":
			return <TravelIcon src="/icons/train.svg" />;
		case "car":
			return <TravelIcon src="/icons/car.svg" />;
		case "flight":
			return <TravelIcon src="/icons/travel.svg" />;
		case "shuttle":
			return <TravelIcon src="/icons/shuttle.svg" />;
	}
};
